import { useRef, useEffect, useCallback } from 'react';

const SCROLL_DEBOUNCE_DELAY = 50;
const THUMBNAIL_WIDTH = 80; // thumbnail width + gap

export default function useScrollManagement(activeIndex, setActiveIndex, productImages, mainSliderRef, thumbnailsRef) {
  const scrollTimeoutRef = useRef(null);
  const programmaticScrollCountRef = useRef(0);

  const updateThumbnailScroll = useCallback((index) => {
    if (thumbnailsRef.current) {
      const containerWidth = thumbnailsRef.current.offsetWidth;
      const scrollLeft = (index * THUMBNAIL_WIDTH) - (containerWidth / 2) + (THUMBNAIL_WIDTH / 2);

      thumbnailsRef.current.scrollTo({
        left: Math.max(0, scrollLeft),
        behavior: 'smooth',
      });
    }
  }, [thumbnailsRef]);

  const handleHeroScroll = useCallback(() => {
    if (programmaticScrollCountRef.current > 0) {
      programmaticScrollCountRef.current -= 1;
      return;
    }

    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    scrollTimeoutRef.current = setTimeout(() => {
      if (mainSliderRef.current && productImages.length > 0) {
        const { scrollLeft } = mainSliderRef.current;
        const { offsetWidth: imageWidth } = mainSliderRef.current;

        const rawIndex = scrollLeft / imageWidth;
        const newIndex = Math.round(rawIndex);
        const clampedIndex = Math.max(0, Math.min(newIndex, productImages.length - 1));

        if (clampedIndex !== activeIndex) {
          setActiveIndex(clampedIndex);
          updateThumbnailScroll(clampedIndex);
        }
      }
    }, SCROLL_DEBOUNCE_DELAY);
  }, [activeIndex, setActiveIndex, productImages, mainSliderRef, updateThumbnailScroll]);

  const scrollToMainImage = (index) => {
    if (mainSliderRef.current) {
      programmaticScrollCountRef.current += 1;

      const { offsetWidth: imageWidth } = mainSliderRef.current;
      mainSliderRef.current.scrollTo({
        left: index * imageWidth,
        behavior: 'auto',
      });

      setActiveIndex(index);
      updateThumbnailScroll(index);
    }
  };

  const scrollToActiveThumbnail = useCallback((index) => {
    updateThumbnailScroll(index);
  }, [updateThumbnailScroll]);

  // Cleanup on unmount
  useEffect(() => () => {
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
  }, []);

  return {
    handleHeroScroll,
    scrollToMainImage,
    scrollToActiveThumbnail,
  };
}
